import { Group, Box, ActionIcon, Text, Avatar, Menu } from '@mantine/core';
import { NavLink, useNavigate } from 'react-router-dom';
import {
  IconSearch,
  IconCalendar,
  IconPlus,
  IconBell,
  IconLogout,
  IconChevronDown,
  IconUser,
  IconLock,
} from '@tabler/icons-react';
import { getProfilePicture } from '../../types/UserDocument';
import { useApp } from '../../context/AppContext';
import { PageRoutes } from '../../constants/routes';
import { primaryColor } from '../../constants/theme';

const menuItems = [
  { icon: <IconSearch />, label: 'Search', path: '/search' },
  { icon: <IconPlus />, label: 'Availability', path: '/availability' },
  { icon: <IconCalendar />, label: 'Bookings', path: '/bookings' },
  {
    icon: <IconBell />,
    label: 'Notifications',
    path: '/notifications',
  },
];

export const Header = () => {
  const { logout, profile } = useApp();
  const navigate = useNavigate();
  const profilePicture = profile?.profilePicture ?? undefined;
  const fullName = profile?.fullName ?? 'Unknown';

  const handleProfile = () => {
    navigate(`${PageRoutes.PROFILE}?professionalId=${profile?.id}`);
  };

  const handleChangePassword = () => {
    navigate('/change-password');
  };

  const handleNotificationSettings = () => {
    navigate(PageRoutes.NOTIFICATION_SETTINGS);
  };

  const handleLogoClick = () => {
    window.location.href = 'https://circlebe.com';
  };

  const links = [
    {
      label: 'Privacy Policy',
      href: 'https://circlebe.com/privacy-policy',
    },
    {
      label: 'Terms & Conditions',
      href: 'https://circlebe.com/terms-and-conditions',
    },
    {
      label: 'Terms of Use',
      href: 'https://circlebe.com/terms-of-use',
    },
  ];

  const visibleMenuItems =
    profile?.isProfessional === false
      ? menuItems.filter((item) => item.path !== PageRoutes.AVAILABILITY)
      : menuItems;

  return (
    <Box
      h={80}
      px="md"
      py="md"
      style={{ backgroundColor: '#fff', borderBottom: '1px solid #e9ecef' }}
    >
      <Group justify="space-between" style={{ height: '100%' }}>
        {/* Logo */}
        <Box
          component="div"
          style={{ cursor: 'pointer' }}
          onClick={handleLogoClick}
        >
          <img
            src="/logo.png"
            alt="CircleBe Logo"
            style={{ height: '32px', width: 'auto' }}
          />
        </Box>

        {/* Menu Items */}
        <Group gap="lg">
          {visibleMenuItems.map((item) => (
            <NavLink
              key={item.label}
              to={item.path}
              style={{
                textDecoration: 'none',
                color: 'inherit',
              }}
            >
              {({ isActive }) => (
                <Box
                  component="div"
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    color: isActive ? primaryColor : '#333',
                    cursor: 'pointer',
                    transition: 'color 200ms',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.color = primaryColor;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.color = isActive
                      ? primaryColor
                      : '#333';
                  }}
                >
                  <ActionIcon variant="subtle" size="lg" color={primaryColor}>
                    {item.icon}
                  </ActionIcon>
                  <Text
                    size="xs"
                    style={{
                      borderBottom: `2px solid ${isActive ? primaryColor : 'transparent'}`,
                      paddingBottom: '2px',
                    }}
                  >
                    {item.label}
                  </Text>
                </Box>
              )}
            </NavLink>
          ))}
        </Group>

        {/* Profile Picture */}
        <Menu position="bottom-end" shadow="md">
          <Menu.Target>
            <ActionIcon
              variant="subtle"
              size="xl"
              color={primaryColor}
              style={{ position: 'relative' }}
            >
              {profilePicture ? (
                <Avatar
                  alt="Profile Picture"
                  src={getProfilePicture(profilePicture)}
                />
              ) : (
                <IconUser size={40} />
              )}
              <IconChevronDown size={16} />
            </ActionIcon>
          </Menu.Target>
          <Menu.Dropdown>
            <Menu.Item
              onClick={handleProfile}
              leftSection={
                profilePicture ? (
                  <Group gap={10}>
                    <Avatar
                      alt="Profile Picture"
                      src={getProfilePicture(profilePicture)}
                      radius="xl"
                      size="sm"
                    />
                    <Text>{fullName}</Text>
                </Group>

                ) : (
                  <Group gap={10}>
                    <IconUser size={24} />
                    <Text>{fullName}</Text>
                  </Group>
                )
              }
            >
            </Menu.Item>
            <Menu.Divider />
            <Menu.Item
              onClick={handleChangePassword}
              leftSection={<IconLock size={16} />}
              onMouseEnter={(e) => {
                e.currentTarget.style.color = primaryColor;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.color = '';
              }}
            >
              Change Password
            </Menu.Item>
            <Menu.Item
              onClick={handleNotificationSettings}
              leftSection={<IconBell size={16} />}
              onMouseEnter={(e) => {
                e.currentTarget.style.color = primaryColor;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.color = '';
              }}
            >
              Notification Settings
            </Menu.Item>
            <Menu.Item
              onClick={logout}
              leftSection={<IconLogout size={16} />}
              onMouseEnter={(e) => {
                e.currentTarget.style.color = primaryColor;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.color = '';
              }}
            >
              Sign Out
            </Menu.Item>
            {links.map((link) => (
              <Menu.Item
                key={link.label}
                component="a"
                href={link.href}
                target="_blank"
                rel="noopener noreferrer"
                onMouseEnter={(e) => {
                  e.currentTarget.style.color = primaryColor;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.color = '';
                }}
              >
                {link.label}
              </Menu.Item>
            ))}
            <Menu.Divider />
            <Menu.Label c="dimmed" ta="center" data-testid="header-version">
              v{__APP_VERSION__}
            </Menu.Label>
          </Menu.Dropdown>
        </Menu>
      </Group>
    </Box>
  );
};
